import { reactive } from 'vue'
import api from "../../api/v2/api"
import { ApiStatus, RequestMode } from "../../api/v2/api/api_constant"
import { ApiError } from "../../api/v2/api/base/response"
import ExamStatusForm from "../../api/v2/forms/answer_status"
import AsForm from "../../api/v2/forms/as"
import Question, { ExamTestQuestion, GroupExamTestQuestion, GroupQuestion } from "../../api/v2/models/question"
import { ExamQScoreHistory } from "../../api/v2/models/q_score_history"
import Exam from "../models/exam"
import { SnackBar } from "../models/other_models"
import UserExam from "../models/user_exam"
import { examState } from "./exam"
import { snackBarState } from "./snackbar"
import { notifyParentOfLearningEvent } from "../learning/learningEventBridge"

class ExamTestState {
    isLoading: boolean = false
    
    // リストのためのデータ
    examTestQuestions: ExamTestQuestion[] = []
    
    // 質問詳細のためのデータ
    groupQuestions: GroupQuestion[] = []

    // 質問履歴のためのデータ
    examQScoreHistory: ExamQScoreHistory | null = null
    
    exam: Exam | null = null
    private userExamId: number = 0
    redirectNotFoundPage: boolean = false

    get examTestIgnoreDescription(){
      return this.examTestQuestions.filter(examTestQuestion => !examTestQuestion.isDescriptionQuestion)
    }

    get examTestQuestionsAnswered(){
      return this.examTestQuestions.filter((item) => item.isAnswered)
    }

    get groupExamTestQuestions() : GroupExamTestQuestion[] {
        let result : GroupExamTestQuestion[] = []
        this.examTestQuestions.forEach(element => {
            let find = result.find(item => item.groupId == element.group)
            if (!find) {
                find = new GroupExamTestQuestion(element.group, [])
                result.push(find)
            }

            find.questions.push(element)
        });
        return result
    }

    setUserExamId(userExamId: number){
        if (this.userExamId != userExamId) {
            this.groupQuestions = []
        }

        this.userExamId = userExamId
    }

    getExamGroupQuestion(questionId: number) {
        return this.groupQuestions.find(item => !!item.questions.find(question => question.id == questionId))
    }

    async requestExamTest(examId: number, getType = "normal"){
        if(!this.exam || this.exam.id != examId){
            this.exam = null
            try {
                this.isLoading = true
                this.exam = await api.exam.showForTest(examId, {}, getType)
            } catch (error) {
                if (error instanceof ApiError) {
                    snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
                    this.redirectNotFoundPage = true
                } else {
                    // Noop
                }
            } finally {
                this.isLoading = false
            }
        }
    }

    async saveAnswer({ answerForm }: { answerForm: AsForm }) {
      let requestMode = RequestMode.TEST
      this.isLoading = true
      if (this.exam.isResultAfterQuestion) {
        requestMode = RequestMode.DETAIL
      }
  
      // アンサーの場合、ExamのTESTモードふぇ取りたいです。パフォーマンスのため
      return api.as.create(answerForm, requestMode).then((questions: Question[]) => {
        this.isLoading = false
        notifyParentOfLearningEvent('assessment_answered')

        // 新しい質問を切り出す
        // Reative効かない場合、新しい質問は新しいGroupQuestionにしないといけません
        let groupQuestion = this.getExamGroupQuestion(parseInt(answerForm.qId))
        if (groupQuestion) {
            groupQuestion.questions = questions
            return groupQuestion
        }
      })
        .catch((error: any) => {
          if (error instanceof ApiError) {
            snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
          } else {
            // Noop
          }
          this.isLoading = false
        })
    }
    async saveAiGrade(asIds: number[], answerForm: AsForm) {
      return api.as.aiGrade(asIds, answerForm.examId, answerForm.qId, answerForm.userExamId).then((questions: Question[]) => {
        let groupQuestion = this.getExamGroupQuestion(parseInt(answerForm.qId))
        if (groupQuestion) {
          groupQuestion.questions = questions
          return groupQuestion
        }
      })
    }
    async saveExamStatus({ examStatusForm }: { examStatusForm: ExamStatusForm }) {
      try {
        this.isLoading = true
        const exam = await api.examStatus.patch(examStatusForm.examId, examStatusForm)
        this.exam = exam
  
        // おすすめテストを再フェッチ
        if (examStatusForm.isFinishExam) {
          examState.getRecommendationList()
          api.as.aiGrade([], examStatusForm.examId, "", examStatusForm.userExamId).then(() => {
            console.log("Grade AI done")
          })
        }
      } catch (error) {
        if (error instanceof ApiError) {
          snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
        } else {
          // Noop
        }
      } finally {
        this.isLoading = false
      }
    }

    setRedirectNotFoundPage({ status }) {
        this.redirectNotFoundPage = status
    }

    async getExamTestQuestions() {
        try {
            this.examTestQuestions = await api.examQuestionApi.get(this.userExamId)

            // 質問回答したら、結果だすの場合、テスト一覧に結果の状態を更新したいですので、質問詳細がないといけないですので、回答した質問を質問詳細を鳥にいく
            if (this.exam.isResultAfterQuestion) {
              this.examTestQuestions.forEach(examTestQuestion => {
                if (examTestQuestion.isAnswered) {
                  this.requestExamGroupQuestion(examTestQuestion.id, true)
                }
              })
            }
        } catch (error) {
            if (error instanceof ApiError) {
                snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
            } else {
                // Noop
            }
        }
    }

    async requestExamGroupQuestion(questionId: number, inBackground = false) {
        let groupQuestion = this.getExamGroupQuestion(questionId)
        if (!groupQuestion && this.userExamId && questionId) {
            try {
              if (!inBackground) {
                this.isLoading = true
              }
              const questions = await api.examQuestionApi.show(this.userExamId, questionId)
              if (questions.length) {
                  groupQuestion = new GroupQuestion(questions[0].group, questions)
                  this.groupQuestions.push(groupQuestion)
              }
            } catch (error) {
              if (error instanceof ApiError) {
                  snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
              } else {
                  // Noop
              }
            } finally {
              if (!inBackground) {
                this.isLoading = false
              }
            }
        }
        return groupQuestion
    }
    async reTest(exam: Exam, examType: string) {
      try {
        this.isLoading = true
        return await api.exam.rejoin(exam.id, examType)
      } catch (error) {
        if (error instanceof ApiError) {
          snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
        } else {
          // Noop
        }
      } finally {
        this.isLoading = false
      }
    }
    async clearAllTest(exam: Exam) {
      return new Promise<boolean>((resolve, reject)=>{
        this.isLoading = true
        api.exam.clearAll(exam.id)
          .then(() => {
            this.isLoading = false
            snackBarState.snackbar = new SnackBar(true, "テスト結果をクリアしました", ApiStatus.SUCCESS)
            resolve(true)
          })
          .catch((error) => {
            if (error instanceof ApiError) {
              snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
            } else {
              // Noop
            }
            this.isLoading = false
            reject(error)
          })
      })
    }
    async deleteUserExam(userExam: UserExam) {
      return new Promise<boolean>((resolve, reject)=>{
        this.isLoading = true
        api.userExam.delete(userExam.id)
          .then(() => {
            this.isLoading = false
            snackBarState.snackbar = new SnackBar(true, "テストをクリアしました", ApiStatus.SUCCESS)
            resolve(true)
          })
          .catch((error) => {
            if (error instanceof ApiError) {
              snackBarState.snackbar = new SnackBar(true, error.messages, error.apiStatus)
            } else {
              // Noop
            }
            this.isLoading = false
            reject(error)
          })
      })
    }
    reset(){
      this.exam = null
      this.examTestQuestions = []
      this.groupQuestions = []
      this.userExamId = 0
      this.examQScoreHistory = null
    }
}


// Wrap in reactive() for Vue 3 reactivity
export const examTestState = reactive(new ExamTestState()) as ExamTestState